
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { Role } from '../../../models/user.models';
import { RoleService } from '../../../services/role.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { RoleEditDialogComponent } from './role-edit-dialog/role-edit-dialog.component';
import { describeApiError } from '../../../utils/api-error.utils';

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent
],
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
})
export class RolesComponent implements OnInit, OnDestroy {
  private roleService = inject(RoleService);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private fb = inject(FormBuilder);

  readonly paginator = viewChild.required(MatPaginator);

  private destroy$ = new Subject<void>();

  // Data
  roles: Role[] = [];
  filteredRoles: Role[] = [];
  dataSource = new MatTableDataSource<Role>([]);
  displayedColumns: string[] = ['name', 'description'];

  // State
  isLoading = false;
  error: string | null = null;

  // Pagination
  totalCount = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];

  // Search
  searchForm!: FormGroup;
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.searchForm = this.fb.group({});
    this.loadRoles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRoles(): void {
    this.isLoading = true;
    this.error = null;

    this.roleService
      .getAllRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          this.roles = roles;
          this.applyFilter(this.searchForm.get('search')?.value || '');
          this.isLoading = false;
        },
        error: () => {
          this.error = 'Failed to load roles. Please try again.';
          this.isLoading = false;
          this.snackbarService.showError('Failed to load roles');
        },
      });
  }

  private applyFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredRoles = [...this.roles];
    } else {
      this.filteredRoles = this.roles.filter(
        (role) =>
          role.name.toLowerCase().includes(term) ||
          (role.description?.toLowerCase().includes(term) ?? false),
      );
    }

    this.totalCount = this.filteredRoles.length;
    this.pageIndex = 0;
    this.updateDataSource();
  }

  private updateDataSource(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.dataSource.data = this.filteredRoles.slice(startIndex, endIndex);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updateDataSource();
  }

  onRowClick(role: Role): void {
    const dialogRef = this.dialog.open(RoleEditDialogComponent, {
      data: { role },
      panelClass: 'role-edit-dialog-panel',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((updatedRole: Role | undefined) => {
      if (updatedRole) {
        // Update the role in the local array (dialog already saved the changes)
        const index = this.roles.findIndex((r) => r.id === updatedRole.id);
        if (index !== -1) {
          this.roles[index] = updatedRole;
          this.applyFilter(this.searchForm.get('search')?.value || '');
        }
      }
    });
  }

  private saveRole(updatedRole: Partial<Role>): void {
    if (!updatedRole.id) return;

    this.roleService
      .updateRole(updatedRole.id, updatedRole)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (savedRole) => {
          // Update the role in the local array
          const index = this.roles.findIndex((r) => r.id === savedRole.id);
          if (index !== -1) {
            this.roles[index] = savedRole;
            this.applyFilter(this.searchQuery || '');
          }
          this.snackbarService.showSuccess('Role updated successfully');
        },
        error: (err) => {
          this.snackbarService.showError(
            describeApiError(err, 'Failed to update role'),
          );
        },
      });
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applyFilter(this.searchQuery);
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter('');
  }
}
